<script setup>
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import { VDataTableServer } from 'vuetify/labs/VDataTable'
import TypeDrawer from '@/views/quality/master/TypeDrawer.vue'
import TypeService from '@/services/quality/type.service'

const searchQuery = ref('')
const debouncedSearch = ref('')
const isConfirmDialogVisible = ref(false)

// Debounce search 500ms so typing doesn't fire a request per keystroke
let searchTimeout = null

watch(searchQuery, val => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    debouncedSearch.value = val
    page.value = 1
  }, 500)
})

definePage({
  meta: {
    name: 'incident-master-type',
    subject: 'quality.menu',
    action: 'master_incident_type',
  },  
})

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

const updateOptions = options => {
  page.value = options.page
  orderBy.value = options.sortBy[0]?.key
  sortBy.value = options.sortBy[0]?.order
}

// Headers
const headers = [
  {
    title: 'Name',
    key: 'name',
  },
  {
    // Icon and colour are what this master configures, so give them a column
    // of their own rather than hanging them off the name.
    title: 'Icon',
    key: 'icon',
    sortable: false,
  },
  {
    title: 'Actions',
    key: 'actions',
    sortable: false,
  },
]

const {
  data: typeListing,
  execute: fetchTypeListing,
} =  await useApi(createUrl('/masters/incident-types', {
  query: {
    paged: 1,
    q: debouncedSearch,
    itemsPerPage,
    page,
    sort: sortBy,
    order_by: orderBy,
  },
}))

const types = computed(() => typeListing.value?.data)
const totalInvestigationGroups = computed(() => typeListing.value?.meta?.total)

const isDrawerVisible = ref(false)
const isViewOnly = ref(false)
const typeData = ref()

const showInvestigationGroup = (data = null, viewOnly = false) => {
  typeData.value = data
  isDrawerVisible.value = true
  isViewOnly.value = viewOnly
}

watch(isDrawerVisible, value => {
  if (value === false) {
    typeData.value = null
    isViewOnly.value = false
  }
})

const refConfirmDialog = ref()

const showConfirmDialog = data => {
  typeData.value = data
  isConfirmDialogVisible.value = true
}

const deleteInvestigationGroup = async (confirm, data) => {
  if(data?.id && confirm){
    TypeService.destroy(
      data.id, 
      () => {
        refConfirmDialog.value.onSuccess()
        isConfirmDialogVisible.value = false
        fetchTypeListing()
      },
      () => {
        refConfirmDialog.value.onError()
      },
    )
  }
}

watch(isConfirmDialogVisible, value => {
  if (value === false) typeData.value = null
})
</script>

<template>
  <section>
    <VCard
      variant="flat"
      class="border"
    >
      <VCardText class="d-flex flex-wrap align-center py-4 gap-4">
        <!-- 👉 Search -->
        <div class="type-search-field">
          <AppTextField
            v-model="searchQuery"
            placeholder="Search"
            density="compact"
          />
        </div>

        <VSpacer />

        <VBtn
          v-if="$can('create', 'master.form_type')"
          prepend-icon="tabler-plus"
          @click="showInvestigationGroup(null)"
        >
          Add Type
        </VBtn>
      </VCardText>

      <VDivider />

      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :items="types"
        :items-length="totalInvestigationGroups"
        :headers="headers"
        class="text-no-wrap"
        @update:options="updateOptions"
      >
        <template #item.name="{ item }">
          <button
            type="button"
            class="type-name text-body-1 font-weight-medium text-start"
            @click="showInvestigationGroup(item, true)"
          >
            {{ item.name }}
          </button>
        </template>

        <template #item.icon="{ item }">
          <VIcon
            v-if="item.icon"
            size="22"
            :icon="item.icon"
            :color="item.color"
          />
          <span
            v-else
            class="text-medium-emphasis"
          >—</span>
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <IconBtn 
            v-if="$can('delete', 'master.form_type')"
            @click="showConfirmDialog(item)"
          >
            <VIcon icon="tabler-trash" />
          </IconBtn>

          <IconBtn 
            v-if="$can('update', 'master.form_type')"
            @click="showInvestigationGroup(item)"
          >
            <VIcon icon="tabler-edit" />
          </IconBtn>
        </template>
      </VDataTableServer>
    </VCard>
    <TypeDrawer
      v-model:isDrawerOpen="isDrawerVisible"
      :form-data="typeData"
      :view-only="isViewOnly"
      @refetch-listing="fetchTypeListing"
    />
    <ConfirmDialog
      ref="refConfirmDialog"
      v-model:isDialogVisible="isConfirmDialogVisible"
      confirmation-question="Are you sure?"
      confirm-msg="Incident Type Deleted."
      confirm-title="Deleted!"
      cancel-title="Error!"
      cancel-msg="Something Went Wrong."
      :is-confirm-visible="false"
      :is-error-visible="false"
      :data="typeData"
      @confirm="deleteInvestigationGroup"
    />
  </section>
</template>

<style>
.v-data-table-footer {
  border-block-start: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  margin-block-start: 0 !important;
  padding-block: 0.5rem;
}
</style>

<style lang="scss" scoped>
.type-search-field {
  inline-size: 100%;

  @media (min-width: 600px) {
    inline-size: 20rem;
  }
}

// Rendered as a real button so the row opens from the keyboard too,
// but styled as plain text to sit naturally in the cell.
.type-name {
  padding: 0;
  border: 0;
  background: none;
  color: rgb(var(--v-theme-on-surface));
  cursor: pointer;
  font: inherit;
  transition: color 0.15s ease;

  &:hover {
    color: rgb(var(--v-theme-primary));
  }
}
</style>
